import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { trackVideoEngagement } from '@/utils/analytics';

const DayGuestExperienceVideo: React.FC = () => {
    const { t } = useLanguage();
    const videoRef = useRef<HTMLVideoElement>(null);

    // Auto-play video when in view
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        video.play().catch((error) => {
                            console.log('Video autoplay prevented:', error);
                        });
                    } else {
                        video.pause();
                    }
                });
            },
            { threshold: 0.5 }
        );

        observer.observe(video);

        return () => {
            observer.disconnect();
        };
    }, []);

    // Video engagement tracking handlers
    const handlePlay = () => {
        trackVideoEngagement('Day Guest Experience', 'play', 0);
    };

    const handlePause = () => {
        const video = videoRef.current;
        if (video) {
            const percent = Math.round((video.currentTime / (video.duration || 1)) * 100);
            trackVideoEngagement('Day Guest Experience', 'pause', percent);
        }
    };

    const handleEnded = () => {
        trackVideoEngagement('Day Guest Experience', 'complete', 100);
    };

    return (
        <section className="relative bg-elegant-white py-20 md:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
            {/* Smooth gradient transition from pebble-beige to white at top */}
            <div className="absolute top-0 left-0 right-0 h-24 md:h-32 bg-gradient-to-b from-pebble-beige to-elegant-white pointer-events-none z-10" />
            
            <div className="max-w-7xl mx-auto relative z-20">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12 md:mb-16"
                >
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-playfair text-charcoal-gray mb-4 leading-tight">
                        {t('activities.experienceVideo.title')}
                    </h2>
                    <p className="text-lg md:text-xl text-charcoal-gray/80 font-lato leading-relaxed max-w-3xl mx-auto">
                        {t('activities.experienceVideo.subtitle')}
                    </p>
                </motion.div>

                {/* Video Container */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="relative w-full rounded-2xl overflow-hidden shadow-2xl"
                >
                    <div className="relative w-full aspect-video bg-charcoal-gray">
                        <video
                            ref={videoRef}
                            className="w-full h-full object-cover"
                            autoPlay
                            loop
                            playsInline
                            preload="none"
                            controls={true}
                            poster="/images/activity arcade/experience-poster-video.webp"
                            onPlay={handlePlay}
                            onPause={handlePause}
                            onEnded={handleEnded}
                        >
                            <source
                                src="/videos/day-guest-experience-highlights-optimized.mp4"
                                type="video/mp4"
                            />
                            Your browser does not support the video tag.
                        </video>
                        {/* Subtle overlay for better video appearance */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none" />
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default DayGuestExperienceVideo;
